import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import MobileBottomNav from './MobileBottomNav';

const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";
const C = { turquesa: '#5dc1b9', moradoClaro: '#AFA9EC', coral: '#eb636b' };

function formatearTiempo(fecha) {
  if (!fecha) return '';
  const diff = Date.now() - new Date(fecha).getTime();
  const min = Math.floor(diff / 60000);
  const hr = Math.floor(diff / 3600000);
  const dia = Math.floor(diff / 86400000);
  if (min < 1) return 'ahora';
  if (min < 60) return `${min}m`;
  if (hr < 24) return `${hr}h`;
  return `${dia}d`;
}

function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

export default function MobileFeedIdeas({ ideas, user, profile, membership, onSelectIdea, onNewIdea, onPublish, onIdeaSaved, publicando }) {
  const navigate = useNavigate();
  const [editorOpen, setEditorOpen] = useState(false);
  const [ideaEditando, setIdeaEditando] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [contenido, setContenido] = useState('');
  const [guardando, setGuardando] = useState(false);

  const isFree = !membership || membership === 'free_observer' || membership === 'tu_radar';

  const abrirNueva = () => {
    if (isFree) { setShowUpgrade(true); return; }
    setIdeaEditando(null);
    setTitulo('');
    setContenido('');
    setEditorOpen(true);
  };

  const abrirEditar = (idea) => {
    if (isFree) { setShowUpgrade(true); return; }
    setIdeaEditando(idea);
    setTitulo(idea.titulo || '');
    setContenido(stripHtml(idea.contenido || ''));
    setEditorOpen(true);
  };

  const handleGuardar = async () => {
    if (!titulo.trim() || guardando) return;
    setGuardando(true);

    let nueva;
    if (ideaEditando) {
      const { data, error } = await supabase.from('pensamientos')
        .update({ titulo: titulo.trim(), contenido: contenido.trim() })
        .eq('id', ideaEditando.id).select().single();
      if (error) { alert('Error: ' + error.message); setGuardando(false); return; }
      nueva = data;
    } else {
      const { data, error } = await supabase.from('pensamientos').insert({
        user_id: user.id, titulo: titulo.trim(), contenido: contenido.trim(),
        visibilidad: 'privado', tags: [],
      }).select().single();
      if (error) { alert('Error: ' + error.message); setGuardando(false); return; }
      nueva = data;

      // Create cluster sinapsis
      await supabase.from('sinapsis').insert({
        pensamiento_from_id: nueva.id, user_from_id: user.id, user_to_id: user.id,
        tipo: 'cluster', score: 1.0,
      });
    }

    setGuardando(false);
    setTitulo('');
    setContenido('');
    setEditorOpen(false);
    setIdeaEditando(null);
    if (onIdeaSaved) onIdeaSaved(nueva, !!ideaEditando);
  };

  // Full-screen editor overlay
  if (editorOpen) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        height: '100dvh', maxHeight: '100dvh',
        background: '#0a0a0f', zIndex: 9999,
        display: 'flex', flexDirection: 'column',
        fontFamily: font, color: '#fff',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}>
        <header style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '0.5px solid rgba(255,255,255,0.1)',
          flexShrink: 0,
        }}>
          <button
            onClick={() => { setEditorOpen(false); setTitulo(''); setContenido(''); setIdeaEditando(null); }}
            style={{
              background: 'rgba(83,74,183,0.25)', border: '0.5px solid rgba(175,169,236,0.5)',
              color: C.moradoClaro, padding: '6px 14px', borderRadius: 8,
              fontSize: 13, cursor: 'pointer', fontFamily: font, fontWeight: 500,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            ← Volver
          </button>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>🔒 Idea privada</span>
          <button
            onClick={handleGuardar}
            disabled={guardando || !titulo.trim()}
            style={{
              background: titulo.trim() ? 'linear-gradient(135deg, #534AB7, #1D9E75)' : 'rgba(255,255,255,0.06)',
              border: 'none', color: titulo.trim() ? '#fff' : 'rgba(255,255,255,0.3)',
              padding: '6px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              cursor: titulo.trim() ? 'pointer' : 'default', fontFamily: font,
              opacity: guardando ? 0.5 : 1,
            }}
          >
            {guardando ? '⏳...' : ideaEditando ? '✅ Guardar' : '💡 Guardar'}
          </button>
        </header>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px', WebkitOverflowScrolling: 'touch', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input
            type="text"
            value={titulo}
            onChange={e => setTitulo(e.target.value)}
            placeholder="Título de la idea..."
            autoFocus
            style={{
              width: '100%', background: 'transparent', border: 'none',
              color: '#fff', fontFamily: serif, fontSize: 24, padding: '16px 0 12px',
              outline: 'none', borderBottom: '0.5px solid rgba(255,255,255,0.1)',
              boxSizing: 'border-box',
            }}
          />

          <textarea
            value={contenido}
            onChange={e => setContenido(e.target.value)}
            placeholder={"Desarrolla tu idea...\n\nTip: Usa [[nombre de otra idea]] para conectar ideas entre sí."}
            style={{
              width: '100%', minHeight: 200, background: 'transparent',
              border: 'none', color: 'rgba(255,255,255,0.9)', fontSize: 15,
              lineHeight: 1.8, padding: '16px 0', outline: 'none', resize: 'none',
              fontFamily: font, boxSizing: 'border-box',
            }}
          />

          {/* Wikilink tip */}
          <div style={{
            marginTop: 12, padding: '12px 14px',
            background: 'rgba(175,169,236,0.06)',
            border: '0.5px solid rgba(175,169,236,0.15)',
            borderRadius: 10,
          }}>
            <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7 }}>
              💡 <strong style={{ color: C.moradoClaro }}>Conexiones:</strong> Escribe <code style={{ background: 'rgba(175,169,236,0.12)', color: C.moradoClaro, padding: '1px 5px', borderRadius: 3 }}>[[título]]</code> para enlazar con otra idea.
            </p>
            <p style={{ margin: '6px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.35)', fontStyle: 'italic' }}>
              Ejemplo: "Esto conecta con [[mi primera idea]] porque..."
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#0a0a0f', minHeight: '100vh', fontFamily: font, color: '#fff', paddingBottom: 80 }}>
      {/* Header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 40,
        background: 'rgba(10,10,15,0.95)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '0.5px solid rgba(255,255,255,0.05)',
      }}>
        <div style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 24 }}>🧠</span>
            <div>
              <h1 style={{ fontFamily: serif, fontSize: 20, margin: 0, fontWeight: 400, color: '#fff' }}>Mis Ideas</h1>
              <p style={{ margin: 0, fontSize: 11, color: C.moradoClaro }}>{ideas.length} ideas atómicas</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={abrirNueva}
              style={{
                background: 'linear-gradient(135deg, #534AB7, #1D9E75)', border: 'none',
                color: '#fff', padding: '6px 14px', borderRadius: 8,
                fontSize: 12, cursor: 'pointer', fontFamily: font, fontWeight: 600,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              + Nueva idea
            </button>
            <button
              onClick={() => navigate('/sala')}
              style={{
                background: 'rgba(83,74,183,0.2)', border: '0.5px solid rgba(175,169,236,0.3)',
                color: C.moradoClaro, padding: '6px 14px', borderRadius: 8,
                fontSize: 12, cursor: 'pointer', fontFamily: font, fontWeight: 500,
              }}
            >
              ← La Sala
            </button>
          </div>
        </div>
      </div>

      {/* Ideas list */}
      <div>
        {ideas.map(idea => {
          const contenido = stripHtml(idea.contenido);
          return (
            <div
              key={idea.id}
              onClick={() => abrirEditar(idea)}
              style={{
                padding: '16px 20px',
                borderBottom: '0.5px solid rgba(255,255,255,0.04)',
                cursor: 'pointer',
              }}
            >
              {/* Title row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <p style={{ margin: 0, fontSize: 16, fontWeight: 600, fontFamily: serif, color: '#fff' }}>
                  {idea.titulo || 'Sin título'}
                </p>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', flexShrink: 0, marginLeft: 8 }}>
                  {formatearTiempo(idea.created_at)}
                </span>
              </div>

              {/* Body preview */}
              <p style={{
                margin: '0 0 8px', fontSize: 14, color: 'rgba(255,255,255,0.5)',
                lineHeight: 1.6, display: '-webkit-box', WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>
                {contenido.substring(0, 140)}{contenido.length > 140 ? '...' : ''}
              </p>

              {/* Tags + actions */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {(idea.tags || []).slice(0, 3).map(tag => (
                    <span key={tag} style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: 50,
                      background: 'rgba(175,169,236,0.1)', color: C.moradoClaro,
                    }}>
                      {tag}
                    </span>
                  ))}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); onPublish(idea); }}
                  disabled={publicando}
                  style={{
                    background: 'rgba(93,193,185,0.1)', border: '0.5px solid rgba(93,193,185,0.25)',
                    borderRadius: 6, padding: '4px 10px', fontSize: 11,
                    color: C.turquesa, cursor: 'pointer', fontFamily: font,
                    opacity: publicando ? 0.5 : 1,
                  }}
                >
                  🚀 Publicar
                </button>
              </div>
            </div>
          );
        })}

        {ideas.length === 0 && (
          <div style={{ textAlign: 'center', padding: '80px 20px' }}>
            <p style={{ fontSize: 40, margin: '0 0 12px' }}>🧠</p>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.4)', margin: '0 0 20px' }}>Todavía no tienes ideas</p>
            <button
              onClick={abrirNueva}
              style={{
                padding: '12px 28px', background: 'linear-gradient(135deg, #534AB7, #1D9E75)',
                color: '#fff', border: 'none', borderRadius: 50, fontSize: 14,
                fontWeight: 500, cursor: 'pointer', fontFamily: font,
              }}
            >
              + Crear tu primera idea
            </button>
          </div>
        )}
      </div>

      {/* FAB - new idea */}
      {ideas.length > 0 && (
        <button
          onClick={abrirNueva}
          style={{
            position: 'fixed', bottom: 90, right: 20, zIndex: 51,
            width: 56, height: 56, borderRadius: '50%', border: 'none',
            background: 'linear-gradient(135deg, #534AB7, #AFA9EC)',
            color: '#fff', fontSize: 26, fontWeight: 700, cursor: 'pointer',
            boxShadow: '0 4px 24px rgba(83,74,183,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          +
        </button>
      )}

      {/* Bottom Nav */}
      <MobileBottomNav active="ideas" navigate={navigate} />

      {/* Upgrade modal for Free Observer */}
      {showUpgrade && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }}>
          <div style={{
            background: '#0a0a0f', border: '0.5px solid rgba(93,193,185,0.3)',
            borderRadius: 20, padding: '32px 24px', maxWidth: 360, width: '100%',
            textAlign: 'center',
          }}>
            <p style={{ fontSize: 48, margin: '0 0 12px' }}>🔒</p>
            <h2 style={{ fontFamily: serif, fontSize: 22, fontWeight: 400, margin: '0 0 8px', color: '#fff' }}>
              Mis Ideas es exclusivo de Doer
            </h2>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, margin: '0 0 20px' }}>
              Tu espacio privado para desarrollar ideas antes de compartirlas con la comunidad. Con conexiones [[]] entre tus pensamientos y el copiloto IA de Genio y Luca.
            </p>
            <p style={{ fontSize: 28, fontWeight: 700, color: C.turquesa, margin: '0 0 20px' }}>
              $139 MXN/mes
            </p>
            <button
              onClick={() => { setShowUpgrade(false); navigate('/membresias'); }}
              style={{
                width: '100%', padding: '14px', border: 'none', borderRadius: 50,
                background: 'linear-gradient(135deg, #5dc1b9, #1D9E75)',
                color: '#0a0a0f', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                fontFamily: font, boxShadow: '0 0 30px rgba(93,193,185,0.3)',
              }}
            >
              Desbloquear Doer
            </button>
            <button
              onClick={() => setShowUpgrade(false)}
              style={{
                width: '100%', padding: '12px', marginTop: 10,
                background: 'none', border: 'none',
                color: 'rgba(255,255,255,0.4)', fontSize: 13, cursor: 'pointer',
                fontFamily: font,
              }}
            >
              Ahora no
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
